import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CalendarDays, BadgeCheck, Gift, MessageSquare, ArrowRight, LayoutGrid, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { fmtDate, fmtTimeRange, money } from "@/lib/format";

const MEMBER_FEATURES = [
  ["Book a session", "/offerings", "Browse every offering and book a time that suits you."],
  ["Events & gatherings", "/events", "Workshops, circles and live events you can join."],
  ["My calendar", "/my-calendar", "All your bookings and events in one calendar."],
  ["Media", "/media", "Live streams, replays and podcasts from our enhancers."],
  ["Memberships", "/memberships", "See your plan or join one for member pricing and perks."],
  ["Packages", "/packages", "Pre-paid bundles of sessions at a better price."],
  ["Gift cards", "/gift-certificate", "Send a gift card, or redeem one you've received."],
  ["Messages", "/messages", "Talk with your enhancers and the team."],
  ["Orders", "/orders", "Track shop purchases and download receipts."],
  ["My learning", "/my-learning", "Courses you've joined and your progress."],
  ["Daily coach", "/coach", "A short daily reflection to support your practice."],
  ["Community", "/community", "Groups and conversations with other members."],
  ["Profile & privacy", "/settings/profile", "Your details, notifications and privacy choices."],
];

export function MemberFeaturesButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="member-features-btn" className="eyw-btn-ghost !py-2 text-sm"><LayoutGrid size={15} /> All features</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto z-[70]" data-testid="member-features-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Everything you can do</DialogTitle>
            <DialogDescription>Pick any feature to go straight there.</DialogDescription>
          </DialogHeader>
          <ul className="grid sm:grid-cols-2 gap-1">
            {MEMBER_FEATURES.map(([label, to, desc]) => (
              <li key={to}>
                <Link to={to} onClick={() => setOpen(false)} data-testid={`member-feature-${to.replace(/\W+/g, "-")}`} className="block rounded-xl px-3 py-2 hover:bg-[#144F75]/[0.06] transition-colors">
                  <span className="text-sm font-medium text-[#1A242B]">{label}</span>
                  <span className="block text-xs text-[#4A5D6B]">{desc}</span>
                </Link>
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Tile({ icon: Icon, title, testid, children, empty }) {
  return (
    <div className="eyw-card p-5" data-testid={testid}>
      <h3 className="text-sm font-semibold text-[#1A242B] mb-3 flex items-center gap-2"><Icon size={16} className="text-[#144F75]" /> {title}</h3>
      {children || <p className="text-sm text-[#8FA89B]">{empty}</p>}
    </div>
  );
}

// Member home: upcoming bookings, memberships, gift card balances, unread messages and browse links.
export default function MemberHome() {
  const { data, isLoading, isError } = useQuery({ queryKey: ["member-home"], queryFn: () => api.get("/me/home-summary").then((r) => r.data), refetchInterval: 60000 });
  if (isLoading) return <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4 mb-10" data-testid="member-home-loading">{[0, 1, 2, 3].map((i) => <div key={i} className="eyw-card h-32 animate-pulse bg-[#F1EEE8]" />)}</div>;
  if (isError || !data) return <p className="text-sm text-[#B45151] mb-8" data-testid="member-home-error">We couldn't load your summary. Refresh to try again.</p>;
  const goto = (id) => document.querySelector(`[data-testid="my-booking-${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  return (
    <section className="grid md:grid-cols-2 xl:grid-cols-4 gap-4 mb-10" data-testid="member-home">
      <Tile icon={CalendarDays} title="Upcoming bookings" testid="member-home-bookings" empty="No upcoming bookings.">
        {data.bookings.length > 0 && (
          <ul className="space-y-2">
            {data.bookings.map((b) => (
              <li key={b.id} className="text-sm" data-testid={`member-home-booking-${b.id}`}>
                <p className="font-medium text-[#1A242B] truncate">{b.title}</p>
                <p className="text-xs text-[#4A5D6B]">{fmtDate(b.date)} · {fmtTimeRange(b.start_time)}</p>
                <button onClick={() => goto(b.id)} className="text-xs text-[#144F75] hover:underline" data-testid={`member-home-manage-${b.id}`}>{b.can_change ? "Reschedule or cancel" : "View"}</button>
              </li>
            ))}
          </ul>
        )}
      </Tile>
      <Tile icon={BadgeCheck} title="Memberships" testid="member-home-memberships" empty={null}>
        {data.memberships.length > 0 ? (
          <ul className="space-y-1.5">{data.memberships.map((m) => <li key={m.id} className="text-sm text-[#1A242B]">{m.name}{m.renews && <span className="block text-xs text-[#8FA89B]">Renews {fmtDate(String(m.renews).slice(0, 10))}</span>}</li>)}</ul>
        ) : <Link to="/memberships" className="text-sm text-[#144F75] hover:underline" data-testid="member-home-join">No active membership. See plans</Link>}
      </Tile>
      <Tile icon={Gift} title="Gift card balances" testid="member-home-giftcards" empty="No gift card balance.">
        {data.gift_cards.length > 0 && (
          <ul className="space-y-1.5">{data.gift_cards.map((g) => <li key={g.id} className="flex justify-between text-sm"><span className="text-[#4A5D6B]">Card ····{g.last4}</span><span className="font-semibold text-[#1A242B]" data-testid={`member-home-gc-${g.id}`}>{money(g.balance)}</span></li>)}</ul>
        )}
      </Tile>
      <Tile icon={MessageSquare} title="Messages" testid="member-home-messages">
        <p className="text-2xl font-serif text-[#1A242B]" data-testid="member-home-unread">{data.unread}</p>
        <Link to="/messages" className="text-sm text-[#144F75] hover:underline">{data.unread ? "unread — open messages" : "No unread messages"}</Link>
      </Tile>
      <div className="md:col-span-2 xl:col-span-4 flex flex-wrap gap-3" data-testid="member-home-browse">
        <Link to="/offerings" data-testid="member-home-browse-offerings" className="eyw-btn !py-2 text-sm"><Sparkles size={15} /> Browse offerings</Link>
        <Link to="/events" data-testid="member-home-browse-events" className="eyw-btn-ghost !py-2 text-sm">Browse events <ArrowRight size={15} /></Link>
      </div>
    </section>
  );
}
